import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import ProductHeaderTitle from "../components/ProductHeaderTitle";
import { useProductsStore } from "src/store/product.state";
import product1 from "@assets/img/noImage.jpg";
import { GraphsService } from "@services/amazon/graph/graph.service";
import {
  getDateRange,
  getRat,
} from "@pages/Compare/components/Products/ProductCard/constants";
import { useProductStore } from "src/store/overviewProduct.state";
import { mockData } from "@pages/Compare/components/Products/mock";
import { CategoriesService } from "@services/amazon/categories/categories.service";
import { useScoreAndStatsStore } from "src/store/review-stats.store";

export interface SpiderDotsResponse {
  total_pos: number;
}
export interface DifferenceList {
  difference_sales: number;
}
export interface PotentialHeightResponse {
  total_potential_sales_increase: number;
}

export interface CategoryData {
  [asin: string]: number | null;
}

interface ProductCardProps {
  price: number | null;
  image: string | null;
  title: string | null;
  isUpdated?: boolean;
  hideTitle?: boolean;
  isSmall?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  price,
  image,
  title,
  isUpdated,
  hideTitle,
  isSmall,
}) => {
  const { productPublic } = useProductsStore();
  const { selectedProduct, selectedProductToCompare } = useProductStore();
  const [ratings, setRatings] = useState<{ [asin: string]: number | null }>({});
  const [spiders, setSpiders] = useState<CategoryData>({});
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  const { setScore, setStats } = useScoreAndStatsStore();

  const lookupScores = (asins: string[]): { [asin: string]: number | null } => {
    const result: { [asin: string]: number | null } = {};
    asins.forEach((asin) => {
      result[asin] = spiders[asin] ?? null;
    });
    return result;
  };

  useEffect(() => {
    if (selectedProductToCompare?.length && Object.keys(spiders).length > 0) {
      const allCompareAsins = selectedProductToCompare
        .flat()
        .map((p) => p.asin);

      const newRatings = lookupScores(allCompareAsins);

      setRatings((prev) => ({
        ...prev,
        ...newRatings,
      }));

      if (selectedProduct?.asin && newRatings[selectedProduct.asin] != null) {
        setScore((prev) => ({
          ...prev,
          [selectedProduct.asin]: newRatings[selectedProduct.asin],
        }));
      }
    }
  }, [selectedProductToCompare, spiders, selectedProduct, setScore]);

  useEffect(() => {
    if (selectedProduct?.asin && Object.keys(spiders).length > 0) {
      const mainAsin = selectedProduct.asin;
      const singleScore = spiders[mainAsin] ?? null;

      setRatings((prev) => ({
        ...prev,
        [mainAsin]: singleScore,
      }));

      setScore((prev) => ({
        ...prev,
        [mainAsin]: singleScore,
      }));
    }
  }, [selectedProduct, spiders, setScore]);

  const fetchCategories = async () => {
    if (!selectedProduct?.asin) {
      setSpiders({});
      return;
    }

    const service = new CategoriesService();
    try {
      const response: CategoryData = await service.getCategories(
        selectedProduct.asin,
      );
      setSpiders(response);
    } catch {
      setSpiders({});
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [selectedProduct]);

  const fetchReviewCount = async () => {
    if (!selectedProduct?.asin) {
      setReviewCount(null);
      return;
    }

    const service = new CategoriesService();
    try {
      const response = await service.getReviewsCount(selectedProduct.asin);

      let maybeCount: number | null;
      if (typeof (response as any).count === "number") {
        maybeCount = (response as any).count;
      } else if (typeof response.data?.count === "number") {
        maybeCount = response.data.count;
      } else {
        maybeCount = null;
      }

      setReviewCount(maybeCount);
    } catch {
      setReviewCount(null);
    }
  };

  useEffect(() => {
    fetchReviewCount();
  }, [selectedProduct]);

  const handleReviewScore = async () => {
    if (!selectedProduct?.asin) {
      setStats({ totalReviews: 0 });
      return;
    }

    const service = new GraphsService();
    const { date_start, date_end } = getDateRange();
    try {
      const spiderDot = await service.getGraph(
        selectedProduct.asin,
        date_start,
        date_end,
      );
      const totalReviews = Array.isArray(spiderDot) ? spiderDot.length : 0;
      setStats({ totalReviews });
    } catch {
      setStats({ totalReviews: 0 });
    }
  };

  useEffect(() => {
    handleReviewScore();
  }, [selectedProduct, setStats]);

  const rat = getRat(selectedProduct, mockData[0]);

  const displayRating: string = (() => {
    if (selectedProduct?.asin && ratings[selectedProduct.asin] != null) {
      return ratings[selectedProduct.asin]!.toFixed(1);
    }
    if (rat != null) {
      return rat.toFixed(1);
    }
    return "N/A";
  })();

  const displayReviewCount = reviewCount ?? "…";

  return (
    <div className={isSmall ? styles.small : ""}>
      {!hideTitle && <ProductHeaderTitle title="External product page" />}

      <div className={styles.productCard}>
        <div className={styles.card}>
          <img
            src={image ?? product1}
            alt="Sugarbear Hair Vitamins"
            className={styles.productImage}
          />

          <div className={styles.productInfo}>
            <p style={{ whiteSpace: "wrap", width: "100%" }}>
              {isUpdated ? (
                <mark>{title}</mark>
              ) : (
                title || "Gummy · 60 Count (Pack of 1)"
              )}
            </p>

            <div className={styles.rating}>
              <span className={styles.ratingValue}>⭐ {displayRating}</span>
              <h3 className={styles.reviewCount}>({displayReviewCount})</h3>
            </div>

            <p className={styles.price}>
              ${price} <span>($0.50/Count)</span>
            </p>
            <p>${productPublic?.price} with Subscribe &amp; Save discount</p>
            {!isSmall && (
              <>
                <p className={styles.prime}>prime</p>
                <p>
                  FREE delivery Thu, Apr 25 on $35 of items shipped by Amazon
                </p>
                <p>Or fastest delivery Today 7 AM - 11 AM</p>
                <p>Small Business</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
